import { brandingToShapeVars } from "./brandingToShapeVars";
const DF_FALLBACK_PRIMARY = "#c2562f";
const DF_FALLBACK_PAGE = "#e9e1d6";
const DF_ON_ACCENT_LIGHT = "#ffffff";
// Must equal --df-ink in tokens.css.
const DF_INK = "#26201a";
// Keep pure black: where white and ink both miss 4.5:1, black clears it by as little as 0.17.
const DF_ON_ACCENT_BLACK = "#000000";
const DF_AA_TEXT_CONTRAST = 4.5;
const DF_AA_NON_TEXT_CONTRAST = 3;
const HEX_RE = /^#[0-9a-fA-F]{6}$/;
function isHex(value) {
    return typeof value === "string" && HEX_RE.test(value);
}
function hexToRgb(hex) {
    return {
        r: parseInt(hex.slice(1, 3), 16),
        g: parseInt(hex.slice(3, 5), 16),
        b: parseInt(hex.slice(5, 7), 16),
    };
}
function channel(n) {
    return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
}
function rgbToHex({ r, g, b }) {
    return `#${channel(r)}${channel(g)}${channel(b)}`;
}
function mix(aHex, bHex, t) {
    const a = hexToRgb(aHex);
    const b = hexToRgb(bHex);
    return rgbToHex({
        r: a.r + (b.r - a.r) * t,
        g: a.g + (b.g - a.g) * t,
        b: a.b + (b.b - a.b) * t,
    });
}
function tint(hex, t) {
    return mix(hex, "#ffffff", t);
}
function shade(hex, t) {
    return mix(hex, "#000000", t);
}
function alpha(hex, a) {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}
function relativeLuminance(hex) {
    const { r, g, b } = hexToRgb(hex);
    const linear = (c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}
function contrastRatio(aHex, bHex) {
    const la = relativeLuminance(aHex);
    const lb = relativeLuminance(bHex);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
function onAccent(accent) {
    const light = contrastRatio(DF_ON_ACCENT_LIGHT, accent);
    const dark = contrastRatio(DF_INK, accent);
    if (Math.max(light, dark) < DF_AA_TEXT_CONTRAST) {
        return DF_ON_ACCENT_BLACK;
    }
    return light >= dark ? DF_ON_ACCENT_LIGHT : DF_INK;
}
function accentSoftText(accent, accentSoft) {
    const shaded = shade(accent, 0.45);
    return contrastRatio(shaded, accentSoft) >= DF_AA_TEXT_CONTRAST ? shaded : DF_INK;
}
// Guard on the segmented track, the darkest solid surface a ring touches (the white card follows).
// A ring on a darker surface needs its own guard: no test would fail.
function focusRing(accent, accentSoft) {
    return contrastRatio(accent, accentSoft) >= DF_AA_NON_TEXT_CONTRAST ? accent : DF_INK;
}
export function brandingToThemeVars(branding) {
    const primary = isHex(branding.primaryColor) ? branding.primaryColor : DF_FALLBACK_PRIMARY;
    const secondary = isHex(branding.secondaryColor) ? branding.secondaryColor : primary;
    const background = isHex(branding.backgroundColor)
        ? branding.backgroundColor
        : DF_FALLBACK_PAGE;
    const accentSoft = tint(primary, 0.9);
    return {
        "--df-accent": primary,
        "--df-on-accent": onAccent(primary),
        "--df-focus-ring": focusRing(primary, accentSoft),
        "--df-accent-soft": accentSoft,
        "--df-accent-soft-border": tint(primary, 0.78),
        "--df-accent-soft-text": accentSoftText(primary, accentSoft),
        "--df-accent-shadow": alpha(primary, 0.3),
        "--df-deep": shade(secondary, 0.82),
        "--df-summary-bg": tint(primary, 0.94),
        "--df-summary-border": tint(primary, 0.86),
        "--df-page-bg": background,
        ...brandingToShapeVars(branding),
    };
}
